import styled from 'styled-components';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, mainTheme } from 'dash-ui';
import { useLocation, useNavigate } from 'react-router-dom';

import { RoutesPath } from 'flow-components/routes/routes';
import { GetPropertyByIdQuery } from 'gql/generated.graphql';
import { getCurrentTabFromSearchParam } from 'common/helpers';
import { AdvancedPropertyDetails } from 'containers/properties/AdvancedPropertyDetails';
import PropertyGeneralInformation from 'containers/properties/PropertyGeneralInformation/PropertyGeneralInformation';
import PropertyFeaturesAndAmenitiesTab from 'containers/properties/PropertyFeaturesAndAmenities/PropertyFeaturesAndAmenitiesTab';
import PropertyInventory from 'containers/properties/PropertyInventory/PropetyInventory';
import PropertyMedia from 'containers/properties/PropertyMedia/PropertyMedia';
import { stickyTopBarStyles } from 'containers/properties/helpers/stickyTopBarStyles';
import PropertyNote from 'containers/properties/PropertyNote/PropertyNote';

import {
  DEFAULT_PROPERTY_TAB_KEY,
  PropertyTabsKeys,
} from './PropertyDetailsTabs.constants';

interface IProps {
  property: GetPropertyByIdQuery['getPropertyById'];
}

const TabsBox = styled(Box)<{ top?: number }>`
  border-top: 1px solid ${mainTheme.colors.n30};
  border-bottom: 1px solid ${mainTheme.colors.n30};
  ${stickyTopBarStyles}
`;

function PropertyDetailsTabs({ property }: IProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = getCurrentTabFromSearchParam(
    location.search
  ) as PropertyTabsKeys;
  const activeTab = currentTab || DEFAULT_PROPERTY_TAB_KEY;
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });

  const tabsMap: Record<PropertyTabsKeys, () => JSX.Element> = {
    [PropertyTabsKeys.INFORMATION]: () => (
      <PropertyGeneralInformation property={property} />
    ),
    [PropertyTabsKeys.ADVANCED]: () => (
      <AdvancedPropertyDetails property={property} />
    ),
    [PropertyTabsKeys.INVENTORY]: () => (
      <PropertyInventory property={property} />
    ),
    [PropertyTabsKeys.FEATURES_AND_AMENITIES]: () => (
      <PropertyFeaturesAndAmenitiesTab property={property} />
    ),
    [PropertyTabsKeys.MEDIA]: () => <PropertyMedia property={property} />,
    [PropertyTabsKeys.NOTES]: () => <PropertyNote property={property} />,
  };

  return (
    <>
      <TabsBox w="100%" pos="sticky" top={219} overflow="visible">
        <Tabs
          variation="secondary"
          tabs={Object.values(PropertyTabsKeys).map((key) => ({
            key,
            label: tCommonLabels(camelCase(key)),
          }))}
          activeTab={activeTab}
          onClick={(key) =>
            navigate(`${RoutesPath.Properties}/${property._id}?tab=${key}`)
          }
        />
      </TabsBox>
      <Box w="100%">{tabsMap[activeTab]() || null}</Box>
    </>
  );
}

export default PropertyDetailsTabs;
